import clsx from 'clsx';
import React from 'react';
import CheckBox from 'components/ui/checkBox/CheckBox';
import { BlazeTableColDef, SelectAllType } from 'components/blazeTable/BlazeTable.types';
import SelectAllDropdown from './SelectAllDropdown';
import _ from 'lodash';
import BzmIcon from '../icons/BzmIcon';
import { SortConfiguration } from '../../types/rest/paginationTypes';
import styles from './BlazeTableHeader.module.css';

interface BlazeTableHeaderProps {
  /** Definition of the table columns */
  blazeTableColDef: BlazeTableColDef[];

  /** If true, row items can be selected by checkbox. */
  isSelectable: boolean;

  /** If true, dropdown with select all visible / select all is shown. */
  showSelectAll: boolean;

  /** If set to true, expand action is shown. */
  showRowExpander: boolean;

  /** Size of the column with the "caret" with expand detail ability. */
  expandColumnSize: number;

  /** Width of the column with select element */
  selectColumnSize: number;

  /** Current value of Select All. */
  selectAllType: SelectAllType | null;
  onSelectAll: (selectAllType?: SelectAllType) => void;
  onDeselectAll: () => void;

  /** If true all items in the table are disabled (cannot be selected etc.) */
  disabled: boolean;

  /** Number of items currently selected in the table */
  selectedItemsCount: number;

  /** If true, drop down with select all records is shown */
  selectAllDropdown?: boolean;

  /** If true, sorting actions are enabled */
  isSortable?: boolean;

  /** Controls which column sorted and the direction of the sort. */
  sorting?: SortConfiguration;

  /** When sorting changes. */
  onChangeSorting?: (sort: SortConfiguration) => void;
}

const BlazeTableHeader = (props: BlazeTableHeaderProps) => {
  const sortByColumnHandler = (accessor: string) => {
    //If sorting is not defined yet (=user hasn't sorted the table)
    const currentDirection = _.isNil(props.sorting) ? 'asc' : props.sorting.direction;
    const direction = currentDirection === 'asc' ? 'desc' : 'asc';
    props.onChangeSorting({ sortKey: accessor, direction: direction });
  };

  /**
   * Content of the header cell
   * @param isSortable
   * @param columnDefinition
   */
  const getCellContent = (isSortable: boolean, columnDefinition: BlazeTableColDef) => {
    // True if current column is being sorted
    const isBeingSorted = _.get(props.sorting, 'sortKey') === columnDefinition.accessor;
    return (
      <>
        <span
          // Temporary Fix of the jumping Caption
          style={{
            bottom: isSortable && isBeingSorted && _.isNil(columnDefinition.headerComponent) ? '-3px' : undefined,
          }}
          className={'position-relative text-truncate'}
        >
          {columnDefinition.headerComponent ? columnDefinition.headerComponent : columnDefinition.header}
        </span>
        {isSortable && isBeingSorted && (
          <div className={'d-inline-flex justify-content-center flex-column'}>
            <span className={'d-inline-block ml-4'}>
              {props.sorting.direction === 'asc' ? (
                <BzmIcon
                  size={13}
                  className={clsx(styles.sortArrow, props.sorting.readonly !== false)}
                  variant={'icon-bzm-arrow-up'}
                />
              ) : (
                <BzmIcon
                  size={13}
                  className={clsx(styles.sortArrow, props.sorting.readonly !== false)}
                  variant={'icon-bzm-arrow-down'}
                />
              )}
            </span>
          </div>
        )}
      </>
    );
  };

  const headerItems = props.blazeTableColDef.map((columnDefinition: BlazeTableColDef, index: number) => {
    const isHeaderPresent = !_.isNil(columnDefinition.headerComponent) || !_.isEmpty(columnDefinition.header);
    const isSortable = isHeaderPresent && props.isSortable === true && columnDefinition.isSortable !== false;
    return (
      <div
        key={index}
        className={clsx(
          'flex-table-col',
          columnDefinition.width && 'flex-table-col-' + columnDefinition.width,
          isSortable && 'hand-cursor',
          isSortable && styles.sortCell,
        )}
        onClick={() => {
          if (isSortable && _.get(props.sorting, 'readonly') !== true) {
            sortByColumnHandler(columnDefinition.accessor);
          }
        }}
        title={columnDefinition.headerComponent ? '' : columnDefinition.header ? columnDefinition.header : ''}
      >
        <div className={'d-flex flex-row'}>{getCellContent(isSortable, columnDefinition)}</div>
      </div>
    );
  });

  return (
    <div className="flex-table-item-heading blaze-table-header">
      <div className="flex-table-row">
        {props.showRowExpander && <div className={'flex-table-col flex-table-col-' + props.expandColumnSize}></div>}

        {/* Show select only if table is selectable */}
        {props.isSelectable && (
          <div className={'flex-table-col flex-table-col-' + props.selectColumnSize}>
            {/* CheckBox for selecting all */}
            {props.showSelectAll && (
              <CheckBox
                checked={props.selectAllType !== null}
                onCheck={() => props.onSelectAll()}
                selectAll={true}
                disabled={props.disabled}
              />
            )}

            {/* Dropdown with caret for selecting all */}
            {props.selectAllDropdown === true && (
              <SelectAllDropdown
                disabled={props.disabled}
                selectedItemsCount={props.selectedItemsCount}
                selectAllType={props.selectAllType}
                onSelect={props.onSelectAll}
                onDeselect={props.onDeselectAll}
              />
            )}
          </div>
        )}

        {headerItems}
      </div>
    </div>
  );
};

export default BlazeTableHeader;
